import React from "react";

import { type ReaderInfo } from "../../services/readerApi";

/**
 * Picks WHICH reader an operation acts on.
 *
 * A SmartBox can carry two or three readers — SB023 runs two 32-port readers
 * on 192.168.51.100 and .101; a 20 ft box runs three, up to 95 antennas. They
 * are separate hardware on separate addresses: one can be dead, or switched
 * off at the box, while the others scan perfectly.
 *
 * So every reader operation targets exactly one of them, and this is where
 * that choice is made. Shared by the map/box-detail card and the ticket panel
 * so the two surfaces cannot drift into offering different readers.
 *
 * On a single-reader box it renders nothing — there is no choice to make, and
 * a picker with one option is just noise.
 *
 * The dot is the same three-state convention as the rest of the reader UI:
 * green = the reader answered, red = it did not, grey = unknown, which for a
 * DISABLED reader is the honest answer rather than a fault. Nothing is
 * dialling a disabled reader, so it is never probed.
 */
interface ReaderPickerProps {
  readers: ReaderInfo[];
  selected: number | null;
  onSelect: (readerId: number) => void;
  disabled?: boolean;
}

const dotClass = (r: ReaderInfo): string => {
  if (!r.is_active) return "bg-secondary-300";
  if (r.reader_responding) return "bg-success-500";
  if (r.reader_responding === false) return "bg-error-500";
  return "bg-secondary-300";
};

export const ReaderPicker: React.FC<ReaderPickerProps> = ({
  readers,
  selected,
  onSelect,
  disabled = false,
}) => {
  if (readers.length <= 1) return null;

  return (
    <div className="mb-3">
      <div className="text-xs font-medium text-secondary-500 mb-1">
        This box has {readers.length} readers — choose which one to work on
      </div>
      <div className="flex flex-wrap gap-2">
        {readers.map((r) => {
          const isSel = r.reader_id === selected;
          return (
            <button
              key={r.reader_id}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(r.reader_id)}
              className={`flex items-center gap-2 text-xs rounded border px-2 py-1 transition ${
                isSel
                  ? "border-primary-500 bg-primary-50 text-primary-900"
                  : "border-secondary-200 text-secondary-700 hover:border-secondary-300"
              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${dotClass(r)}`} />
              <span className="font-medium">Reader {r.reader_id}</span>
              <span className="text-secondary-500">{r.ip_address}</span>
              <span className="text-secondary-500">
                {r.antenna_count} ant
              </span>
              {!r.is_active && (
                <span className="text-secondary-500 italic">off</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Why a disabled reader shows no health verdict. Rendered under the picker
 *  when the selected reader is switched off at the box, because "unreachable"
 *  would otherwise read as a fault on a box that is behaving correctly. */
export const DisabledReaderNote: React.FC<{ reader: ReaderInfo }> = ({ reader }) => (
  <div className="text-xs text-secondary-700 bg-secondary-50 border border-secondary-200 px-3 py-2 rounded mb-3">
    The box has reader {reader.reader_id} switched off (<code>reader.is_active = 0</code>),
    so nothing is dialling it and it was not probed. That is one switch, not{" "}
    {reader.antenna_count} dead antennas — if this reader should be scanning, the
    fix is on the box, not the hardware.
  </div>
);

export default ReaderPicker;
